import { userRoles } from "../config";

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === "") return [];
  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [value];
};

const normalizeValue = (value) =>
  value === undefined || value === null
    ? ""
    : String(value).trim().toLowerCase();

const uniqueBy = (items, getKey) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getUserDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
  user?.fullName ||
  user?.email ||
  "Unknown User";

export const getTeamNamesFromUser = (user) => {
  const rawValues = [
    ...toArray(user?.teamNames),
    ...toArray(
      user?.teams?.map?.((team) => team?.teamName || team?.name || team),
    ),
    ...toArray(user?.team),
  ];

  return uniqueBy(
    rawValues
      .flatMap((value) =>
        typeof value === "string" ? value.split(",") : [value],
      )
      .map((value) => String(value || "").trim())
      .filter(Boolean),
    (value) => normalizeValue(value),
  );
};

export const getManagerNamesFromTeam = (team) => {
  const rawValues = [
    ...toArray(team?.managerNames),
    ...toArray(team?.managers?.map?.((manager) => getUserDisplayName(manager))),
    ...toArray(team?.managerName),
  ];

  return uniqueBy(
    rawValues.map((value) => String(value || "").trim()).filter(Boolean),
    (value) => normalizeValue(value),
  );
};

const getManagerEmailsFromTeam = (team) => {
  const rawValues = [
    ...toArray(team?.managerEmails),
    ...toArray(team?.managers?.map?.((manager) => manager?.email)),
    ...toArray(team?.managerEmail),
  ];

  return uniqueBy(
    rawValues.map((value) => String(value || "").trim()).filter(Boolean),
    (value) => normalizeValue(value),
  );
};

const getManagerIdsFromTeam = (team) =>
  uniqueBy(
    [
      ...toArray(team?.managerIds),
      ...toArray(team?.managers?.map?.((manager) => manager?.id)),
      ...toArray(team?.managerId),
    ].filter(Boolean),
    (value) => String(value),
  );

export const isAdminUser = (user) => user?.role === userRoles.admin;

export const canManageTeamDashboard = (team, currentUser) => {
  if (!team || !currentUser) return false;
  const userId = currentUser?.id;
  const userEmail = normalizeValue(currentUser?.email);
  const userName = normalizeValue(getUserDisplayName(currentUser));

  return (
    getManagerIdsFromTeam(team).some((id) => String(id) === String(userId)) ||
    getManagerEmailsFromTeam(team).some(
      (email) => normalizeValue(email) === userEmail,
    ) ||
    getManagerNamesFromTeam(team).some(
      (name) => normalizeValue(name) === userName,
    )
  );
};

export const canManagersViewEachOther = (team) =>
  !!(
    team?.allowManagersToViewEachOtherData ||
    team?.allowManagerPeerAccess ||
    team?.allowManagersToViewEachOthersData
  );

export const getDashboardAccessibleUsers = ({
  currentUser,
  users = [],
  teams = [],
}) => {
  if (!currentUser?.id) return [];

  if (isAdminUser(currentUser)) {
    return uniqueBy(
      users.filter((user) => user?.id),
      (user) => String(user.id),
    );
  }

  const managedTeams = teams.filter((team) =>
    canManageTeamDashboard(team, currentUser),
  );
  if (!managedTeams.length) {
    return uniqueBy([currentUser], (user) => String(user.id));
  }

  const visibleUserIds = new Set([String(currentUser.id)]);

  users.forEach((candidate) => {
    const candidateTeamNames =
      getTeamNamesFromUser(candidate).map(normalizeValue);
    const sharedTeams = managedTeams.filter((team) =>
      candidateTeamNames.includes(normalizeValue(team?.teamName || team?.name)),
    );

    if (!sharedTeams.length) return;

    const isPeerManager = sharedTeams.some((team) =>
      canManageTeamDashboard(team, candidate),
    );
    const canViewPeerManager = sharedTeams.some((team) =>
      canManagersViewEachOther(team),
    );

    if (
      !isPeerManager ||
      canViewPeerManager ||
      candidate?.id === currentUser?.id
    ) {
      visibleUserIds.add(String(candidate.id));
    }
  });

  return uniqueBy(
    [
      currentUser,
      ...users.filter((user) => visibleUserIds.has(String(user.id))),
    ],
    (user) => String(user?.id),
  );
};
